import React from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import withStyles from "@material-ui/core/styles/withStyles";
import Grid from "@material-ui/core/Grid";

import SponsorsCarouselSection from "./SponsorsCarouselSection";
import { palette, typography } from "../../../theme/brand";

// Unlinked from `material-kit-pro-react`: site footer rebuilt with our own dark
// chrome + MUI v3 Grid + brand styles (dropped template `Footer`/`Grid`/`Button`/
// `footerStyle`). NOTE: the embedded SponsorsCarouselSection is still template-
// coupled and is migrated in a dedicated follow-up.
const SOCIAL_LINKS = [
  {
    href: "https://www.facebook.com/vivamoselcampo/",
    icon: "fab fa-facebook-square",
    label: "Facebook"
  },
  {
    href: "https://www.instagram.com/vivamoselcampo/",
    icon: "fab fa-instagram",
    label: "Instagram"
  },
  {
    href: "https://twitter.com/vivamoselcampo",
    icon: "fab fa-twitter",
    label: "Twitter"
  },
  {
    href: "https://www.youtube.com/channel/UCUvSqN1FljzYH4MQpfQ1fzw",
    icon: "fab fa-youtube",
    label: "YouTube"
  }
];

const BOTTOM_LINKS = [
  { to: "/accommodations", label: "Alojamientos" },
  { to: "/tours", label: "Tours" },
  { to: "/terminos-y-condiciones", label: "Condiciones" },
  { to: "/quienes-somos", label: "La Empresa" },
  { to: "/contacto", label: "Contacto" }
];

const lightText = "rgba(255, 255, 255, 0.78)";

const styles = {
  footer: {
    backgroundColor: "#2E2A26",
    color: lightText,
    paddingTop: "60px"
  },
  container: {
    maxWidth: "1140px",
    margin: "0 auto",
    padding: "0 24px"
  },
  heading: {
    color: "#fff",
    fontFamily: typography.fontFamilyDisplay,
    fontWeight: 700,
    fontSize: "1.1rem",
    margin: "0 0 18px"
  },
  linksVertical: {
    listStyle: "none",
    padding: 0,
    margin: 0,
    "& li": {
      marginBottom: "10px"
    },
    "& a": {
      color: lightText,
      textDecoration: "none",
      transition: "color 0.2s ease",
      "&:hover": {
        color: palette.accent
      }
    }
  },
  socialList: {
    listStyle: "none",
    padding: 0,
    margin: "0 0 28px",
    display: "flex",
    gap: "12px"
  },
  socialLink: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    color: "#fff",
    fontSize: "18px",
    textDecoration: "none",
    transition: "background-color 0.2s ease, transform 0.2s ease",
    "&:hover": {
      backgroundColor: palette.accent,
      transform: "translateY(-3px)"
    }
  },
  contactItem: {
    display: "flex",
    alignItems: "center",
    color: lightText,
    fontSize: "0.95rem",
    fontWeight: 400,
    margin: "0 0 12px",
    "& i": {
      width: "20px",
      marginRight: "10px",
      color: palette.accent
    }
  },
  divider: {
    border: 0,
    borderTop: "1px solid rgba(255, 255, 255, 0.15)",
    margin: "48px 0 24px"
  },
  bottomBar: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    paddingBottom: "28px"
  },
  bottomLinks: {
    listStyle: "none",
    display: "flex",
    flexWrap: "wrap",
    gap: "20px",
    padding: 0,
    margin: 0,
    "& a": {
      color: lightText,
      textDecoration: "none",
      fontSize: "0.9rem",
      transition: "color 0.2s ease",
      "&:hover": {
        color: palette.accent
      }
    }
  },
  copyright: {
    color: "rgba(255, 255, 255, 0.55)",
    fontSize: "0.9rem"
  }
};

function FooterSection({ classes }) {
  return (
    <footer className={classes.footer}>
      <div className={classes.container}>
        <Grid container spacing={32}>
          <Grid item xs={12} md={4}>
            <h4 className={classes.heading}>Síguenos en</h4>
            <ul className={classes.socialList}>
              {SOCIAL_LINKS.map(social => (
                <li key={social.label}>
                  <a
                    className={classes.socialLink}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.label}
                  >
                    <i className={social.icon} />
                  </a>
                </li>
              ))}
            </ul>

            <h4 className={classes.heading}>Contacto</h4>
            <p className={classes.contactItem}>
              <i className="fas fa-phone" /> 315 795 1330 - 313 208 9988
            </p>
            <p className={classes.contactItem}>
              <i className="fas fa-envelope" /> vivamoselcampo@gmail.com
            </p>
            <p className={classes.contactItem}>
              <i className="fas fa-map-marker-alt" /> Cra 7 #1B-36, barrio La
              Florida, Tunja, Boyacá
            </p>
          </Grid>

          <Grid item xs={6} md={2}>
            <h4 className={classes.heading}>Explora</h4>
            <ul className={classes.linksVertical}>
              <li>
                <Link to="/accommodations">Alojamientos</Link>
              </li>
              <li>
                <Link to="/tours">Tours</Link>
              </li>
              <li>
                <Link to="/contenido-360">Contenido 360</Link>
              </li>
            </ul>
          </Grid>

          <Grid item xs={6} md={2}>
            <h4 className={classes.heading}>Usuarios</h4>
            <ul className={classes.linksVertical}>
              <li>
                <Link to="/login">Iniciar sesión</Link>
              </li>
              <li>
                <Link to="/signup">Registrarse</Link>
              </li>
            </ul>
            <h4 className={classes.heading} style={{ marginTop: "24px" }}>
              Legal
            </h4>
            <ul className={classes.linksVertical}>
              <li>
                <Link to="/terminos-y-condiciones">Términos y Condiciones</Link>
              </li>
            </ul>
          </Grid>

          <Grid item xs={12} md={4}>
            <h4 className={classes.heading}>Contamos con el apoyo de</h4>
            <SponsorsCarouselSection />
          </Grid>
        </Grid>

        <hr className={classes.divider} />

        <div className={classes.bottomBar}>
          <ul className={classes.bottomLinks}>
            {BOTTOM_LINKS.map(link => (
              <li key={link.to}>
                <Link to={link.to}>{link.label}</Link>
              </li>
            ))}
          </ul>
          <div className={classes.copyright}>
            &copy; {new Date().getFullYear()} Vivamos el Campo. Todos los
            derechos reservados.
          </div>
        </div>
      </div>
    </footer>
  );
}

FooterSection.propTypes = {
  classes: PropTypes.object.isRequired
};

export default withStyles(styles)(FooterSection);



// WEBPACK FOOTER //
// ./src/views/LandingPage/Sections/FooterSection.jsx